import { Immutable } from "immer";
import React from "react";

import {
    IReviewsProductTypeID,
    isoReviewsProductID,
} from "../../../models/nominals";
import {
    IReviewQueryFacets,
    IReviewsProduct,
    ISearchFacet,
} from "../../../models/reviews";
import { useAppSelector } from "../../../store/reducers";
import { groupBy, localeSortBy, sortBy } from "../../../utils/functional";
import { IFacetCollection, ProductTypeFilter } from "../ProductTypeFilter";
import { defaultState } from "../defaults";

const FACET_TYPE: IReviewQueryFacets = "product_id";

interface IProductType {
    id: IReviewsProductTypeID;
    name: string;
    sort_order: number;
    collections: IFacetCollection[];
}

function getProductTypeOptionCollections(
    productTypeID: IReviewsProductTypeID,
    products: ReadonlyArray<IReviewsProduct>,
    productFacet: ISearchFacet | null,
): IFacetCollection[] {
    // Get all the products for the given type
    const filteredProducts = products.filter(
        (product) => product.product_type === productTypeID,
    );
    const productIDs = filteredProducts.map((product) => product.id);
    // Limit the product facet options to just products of the given type
    const allOptions = productFacet ? productFacet.options : [];
    const options = allOptions.filter((option) =>
        productIDs.includes(isoReviewsProductID.wrap(option.option_id)),
    );
    // Group the product options into collections, sorted by sort_order, then name.
    const collections = groupBy(
        sortBy(
            sortBy(localeSortBy(options, "group_name"), "group_id"),
            "group_sort_order",
        ),
        "group_id",
    );
    // Return the non-empty collections
    const result: IFacetCollection[] = [];
    for (const collection of collections) {
        if (collection.length > 0) {
            result.push({
                name: collection[0].group_name,
                options: collection,
            });
        }
    }
    return result;
}

function getProductTypes(
    products: ReadonlyArray<IReviewsProduct>,
    productFacet: ISearchFacet | null,
    displayedProductTypeIDWhitelist: readonly IReviewsProductTypeID[],
): IProductType[] {
    // Build de-duplicated map of product type data
    const productTypeMap = new Map<IReviewsProductTypeID, IProductType>();
    for (const product of products) {
        if (
            !product.product_type ||
            (displayedProductTypeIDWhitelist.length > 0 &&
                !displayedProductTypeIDWhitelist.includes(
                    product.product_type,
                )) ||
            productTypeMap.has(product.product_type)
        ) {
            continue;
        }
        productTypeMap.set(product.product_type, {
            id: product.product_type,
            name: product.product_type_name || "",
            sort_order: product.product_type_sort_order,
            collections: getProductTypeOptionCollections(
                product.product_type,
                products,
                productFacet,
            ),
        });
    }
    // Convert map into a sorted array
    const productTypes = sortBy([...productTypeMap.values()], "sort_order");
    return productTypes;
}

export const ProductTypeFilterSection = (
    props: Immutable<{
        displayedProductTypeIDWhitelist: readonly IReviewsProductTypeID[];
        isCarouselComponentType?: boolean;
        isCollapsedView?: boolean;
        isCondensedView?: boolean;
    }>,
): React.ReactNode => {
    const state = useAppSelector(
        (rootState) => rootState.reviews || defaultState,
    );

    const products = state.data.products;

    const productFacet =
        state.data.facets.find((facet) => facet.type === FACET_TYPE) || null;

    const productTypes = getProductTypes(
        products,
        productFacet,
        props.displayedProductTypeIDWhitelist,
    );

    return (
        <div>
            {productTypes.map((productType) => (
                <ProductTypeFilter
                    key={`${productType.id}`}
                    facetType={FACET_TYPE}
                    productTypeID={productType.id}
                    name={productType.name}
                    collections={productType.collections}
                    isCarouselComponentType={!!props.isCarouselComponentType}
                    isCollapsedView={props.isCollapsedView}
                    isCondensedView={props.isCondensedView}
                />
            ))}
        </div>
    );
};
